import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type {
  ResizableLayoutDirection,
  ResizableLayoutProps,
  ResizableLayoutSize,
} from './types';

import {
  DEFAULT_MIN_HEIGHT,
  DEFAULT_MIN_WIDTH,
} from '../../constants/resizableLayout';
import { KEYBOARD_STEP_PX } from './constants';
import {
  clampSize,
  type ResolveAxisMaxOptions,
  resolveAxisMaxPx,
  roundPx,
} from './utils';

type ResizableAxisDefault = number | 'auto' | undefined;

type DragState = {
  pointerId: number;
  startPointer: number;
  startSize: number;
};

type UseResizableLayoutParams = {
  children: React.ReactNode;
  defaultHeight?: ResizableAxisDefault;
  defaultWidth?: ResizableAxisDefault;
  direction: ResizableLayoutDirection;
  maxHeight?: number | 'available';
  maxWidth?: number | 'available';
  minHeight?: number;
  minWidth?: number;
  onResize: (size: ResizableLayoutSize) => void;
  __private?: ResizableLayoutProps['__private'];
};

const KEYBOARD_DELTAS: Record<
  ResizableLayoutDirection,
  Readonly<Record<string, number>>
> = {
  left: { ArrowLeft: KEYBOARD_STEP_PX, ArrowRight: -KEYBOARD_STEP_PX },
  right: { ArrowLeft: -KEYBOARD_STEP_PX, ArrowRight: KEYBOARD_STEP_PX },
  top: { ArrowUp: KEYBOARD_STEP_PX, ArrowDown: -KEYBOARD_STEP_PX },
};

const isNumericDefault = (value: ResizableAxisDefault): value is number =>
  typeof value === 'number';

const readAxisPx = (
  element: HTMLDivElement | null,
  isWidth: boolean,
  min: number,
): number => {
  const domAxis = isWidth ? element?.offsetWidth : element?.offsetHeight;
  return roundPx(domAxis ?? min);
};

export type ResizableLayoutPanel = {
  containerRef: RefObject<HTMLDivElement>;
  containerClassName: string;
  containerStyle: {
    width?: string;
    height?: string;
    maxWidth?: string;
    maxHeight?: string;
  };
  indicatorProps: {
    ariaValueMax?: number;
    ariaValueMin: number;
    ariaValueNow: number;
    direction: ResizableLayoutDirection;
    isDragging: boolean;
    onDoubleClick: () => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
    onLostPointerCapture: (event: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  };
  isDragging: boolean;
};

export const useResizableLayout = ({
  children,
  defaultHeight,
  defaultWidth,
  direction,
  maxHeight,
  maxWidth,
  minHeight = DEFAULT_MIN_HEIGHT,
  minWidth = DEFAULT_MIN_WIDTH,
  onResize,
  __private,
}: UseResizableLayoutParams): ResizableLayoutPanel => {
  const isWidthAxis = direction === 'left' || direction === 'right';
  const defaultSize = isWidthAxis ? defaultWidth : defaultHeight;
  const defaultAxis = isWidthAxis ? defaultWidth : defaultHeight;
  const min = isWidthAxis ? minWidth : minHeight;
  const max = isWidthAxis ? maxWidth : maxHeight;
  const isMaxAvailable = max === 'available';

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const pendingResetEmitRef = useRef(false);
  const onResizeRef = useRef(onResize);
  const controlledSizeRef = useRef<number | null>(null);
  const lastResolvedMaxRef = useRef<number | undefined>(undefined);
  const isDraggingRef = useRef(false);
  // Whether the size came from the person rather than the default, so a later
  // default can be adopted without undoing their drag. Automatic clamping to
  // `max` does not count.
  const hasUserSizedRef = useRef(false);

  onResizeRef.current = onResize;

  const [isDragging, setIsDragging] = useState(false);
  const [controlledSize, setControlledSize] = useState<number | null>(() =>
    isNumericDefault(defaultSize) ? defaultSize : null,
  );
  const [liveSize, setLiveSize] = useState(() =>
    isNumericDefault(defaultSize) ? defaultSize : min,
  );
  const [resolvedMaxPx, setResolvedMaxPx] = useState<number | undefined>(
    undefined,
  );

  controlledSizeRef.current = controlledSize;

  // Adopt a default that arrives after mount, until the person sizes this
  // themselves. A consumer restoring a size it had to fetch only knows it once
  // that request resolves, which can land after this mounted, so seeding state
  // from the first render alone would drop it. No `onResize` here: nothing was
  // resized, and a consumer persisting the callback would write the value it
  // just supplied back to itself.
  useLayoutEffect(() => {
    if (hasUserSizedRef.current) {
      return;
    }
    setControlledSize(isNumericDefault(defaultSize) ? defaultSize : null);
  }, [defaultSize]);

  const resolveMaxPx = useCallback(
    (options?: ResolveAxisMaxOptions): number | undefined => {
      return resolveAxisMaxPx(
        containerRef.current,
        isWidthAxis ? 'width' : 'height',
        max,
        options,
      );
    },
    [isWidthAxis, max],
  );

  const clampAxis = useCallback(
    (value: number, reserveSiblingSize: 'current' | 'min' = 'current') =>
      clampSize(value, min, resolveMaxPx({ reserveSiblingSize })),
    [min, resolveMaxPx],
  );

  const measureSize = useCallback((): ResizableLayoutSize => {
    const element = containerRef.current;
    const measuredWidth = element?.offsetWidth;
    const measuredHeight = element?.offsetHeight;

    const resolveWidth = (): number | 'auto' => {
      if (isWidthAxis && controlledSize !== null) {
        return controlledSize;
      }
      if (defaultWidth === 'auto' && controlledSize === null) {
        return 'auto';
      }
      if (measuredWidth != null) {
        return roundPx(measuredWidth);
      }
      if (isNumericDefault(defaultWidth)) {
        return defaultWidth;
      }
      return minWidth;
    };

    const resolveHeight = (): number | 'auto' => {
      if (!isWidthAxis && controlledSize !== null) {
        return controlledSize;
      }
      if (defaultHeight === 'auto' && controlledSize === null) {
        return 'auto';
      }
      if (measuredHeight != null) {
        return roundPx(measuredHeight);
      }
      if (isNumericDefault(defaultHeight)) {
        return defaultHeight;
      }
      return minHeight;
    };

    return { width: resolveWidth(), height: resolveHeight() };
  }, [
    controlledSize,
    defaultHeight,
    defaultWidth,
    isWidthAxis,
    minHeight,
    minWidth,
  ]);

  const toResizeEvent = useCallback(
    (axisSize: number, measured: ResizableLayoutSize): ResizableLayoutSize =>
      isWidthAxis
        ? { width: axisSize, height: measured.height }
        : { width: measured.width, height: axisSize },
    [isWidthAxis],
  );

  useLayoutEffect(() => {
    const measuredAxis = readAxisPx(containerRef.current, isWidthAxis, min);

    if (controlledSize === null) {
      setLiveSize((current) =>
        current === measuredAxis ? current : measuredAxis,
      );
    }

    if (pendingResetEmitRef.current && controlledSize === null) {
      pendingResetEmitRef.current = false;
      onResizeRef.current(measureSize());
    }
  }, [children, controlledSize, isWidthAxis, measureSize, min]);

  useLayoutEffect(() => {
    const element = containerRef.current;
    const parent = element?.parentElement;

    if (!element || !parent || max === undefined) {
      setResolvedMaxPx(undefined);
      return;
    }

    const syncMax = () => {
      // `min` (not current): flex siblings can shrink so this layout can grow.
      const maxPx = resolveAxisMaxPx(
        element,
        isWidthAxis ? 'width' : 'height',
        max,
        { reserveSiblingSize: 'min' },
      );

      if (lastResolvedMaxRef.current !== maxPx) {
        lastResolvedMaxRef.current = maxPx;
        setResolvedMaxPx(maxPx);
      }

      if (isDraggingRef.current || maxPx === undefined || maxPx < min) {
        return;
      }

      const current = controlledSizeRef.current;
      const isFlexManagedAuto = defaultAxis === 'auto' && current === null;

      if (isFlexManagedAuto) {
        return;
      }

      if (current !== null && current > maxPx) {
        const clamped = clampSize(current, min, maxPx);
        setControlledSize(clamped);
        setLiveSize(clamped);
        onResizeRef.current(toResizeEvent(clamped, measureSize()));
      }
    };

    const observer = new ResizeObserver(syncMax);

    observer.observe(parent);
    Array.from(parent.children).forEach((child) => {
      if (child !== element) {
        observer.observe(child);
      }
    });
    syncMax();

    return () => observer.disconnect();
  }, [
    children,
    controlledSize,
    defaultAxis,
    isWidthAxis,
    max,
    min,
    measureSize,
    toResizeEvent,
  ]);

  const clampToMax = useCallback(() => {
    if (max === undefined) {
      return;
    }

    const maxPx = resolveMaxPx({ reserveSiblingSize: 'min' });

    if (maxPx === undefined || maxPx < min) {
      return;
    }

    const current = controlledSizeRef.current;
    const isFlexManagedAuto = defaultAxis === 'auto' && current === null;

    if (isFlexManagedAuto || current === null || current <= maxPx) {
      return;
    }

    const clamped = clampSize(current, min, maxPx);
    setControlledSize(clamped);
    setLiveSize(clamped);
    onResizeRef.current(toResizeEvent(clamped, measureSize()));
  }, [defaultAxis, max, measureSize, min, resolveMaxPx, toResizeEvent]);

  const emitAxisResize = useCallback(
    (nextAxisSize: number) => {
      const measured = measureSize();
      const clamped = clampAxis(nextAxisSize, 'min');

      hasUserSizedRef.current = true;
      setControlledSize(clamped);
      onResizeRef.current(toResizeEvent(clamped, measured));
    },
    [clampAxis, measureSize, toResizeEvent],
  );

  const stopDragging = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      dragStateRef.current = null;
      isDraggingRef.current = false;
      setIsDragging(false);

      clampToMax();

      if (
        event.currentTarget.hasPointerCapture?.(event.pointerId) &&
        event.currentTarget.releasePointerCapture
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [clampToMax],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();

      const axisSize = readAxisPx(containerRef.current, isWidthAxis, min);

      dragStateRef.current = {
        pointerId: event.pointerId,
        startPointer: isWidthAxis ? event.clientX : event.clientY,
        startSize: axisSize,
      };

      isDraggingRef.current = true;
      setIsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [isWidthAxis, min],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      const currentPointer = isWidthAxis ? event.clientX : event.clientY;
      const pointerDelta = currentPointer - dragState.startPointer;
      const delta = direction === 'right' ? pointerDelta : -pointerDelta;

      emitAxisResize(dragState.startSize + delta);
    },
    [direction, emitAxisResize, isWidthAxis],
  );

  const resetSize = useCallback(() => {
    hasUserSizedRef.current = true;

    if (!isNumericDefault(defaultSize)) {
      setControlledSize(null);
      pendingResetEmitRef.current = true;
      return;
    }

    const clamped = clampAxis(defaultSize, 'min');
    setControlledSize(clamped);
    const measured = measureSize();
    onResizeRef.current(toResizeEvent(clamped, measured));
  }, [clampAxis, defaultSize, measureSize, toResizeEvent]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Home') {
        event.preventDefault();
        resetSize();
        return;
      }

      const delta = KEYBOARD_DELTAS[direction][event.key];

      if (delta === undefined) {
        return;
      }

      event.preventDefault();
      const axisSize = readAxisPx(containerRef.current, isWidthAxis, min);
      emitAxisResize(axisSize + delta);
    },
    [direction, emitAxisResize, isWidthAxis, min, resetSize],
  );

  const isAutoAxis = controlledSize === null && defaultAxis === 'auto';
  const ariaValueNow = Math.max(min, controlledSize ?? liveSize);
  const ariaValueMax = resolveMaxPx({ reserveSiblingSize: 'min' });
  // Applies to a numeric `max` too, not just `available`: without it a panel
  // the consumer capped can still be laid out past that cap by flex — most
  // visibly an `auto` panel, which the controlled-size path leaves alone.
  const cssMaxPx =
    resolvedMaxPx !== undefined && resolvedMaxPx >= min
      ? resolvedMaxPx
      : undefined;

  let resizeStateClass = '';
  if (controlledSize !== null) {
    resizeStateClass = isWidthAxis
      ? 'eds-resizable-layout--resized-width'
      : 'eds-resizable-layout--resized-height';
  } else if (isAutoAxis) {
    resizeStateClass = isWidthAxis
      ? 'eds-resizable-layout--auto-width'
      : 'eds-resizable-layout--auto-height';
  }

  return {
    containerRef,
    containerClassName: [
      'eds-resizable-layout',
      `eds-resizable-layout--direction-${direction}`,
      isMaxAvailable ? 'eds-resizable-layout--max-available' : '',
      __private?.clipOverflow ? 'eds-resizable-layout--clip-overflow' : '',
      __private?.sizeToContent ? 'eds-resizable-layout--size-to-content' : '',
      resizeStateClass,
    ]
      .filter(Boolean)
      .join(' '),
    containerStyle: isWidthAxis
      ? {
          width: controlledSize !== null ? `${controlledSize}px` : undefined,
          maxWidth: cssMaxPx !== undefined ? `${cssMaxPx}px` : undefined,
        }
      : {
          height: controlledSize !== null ? `${controlledSize}px` : undefined,
          maxHeight: cssMaxPx !== undefined ? `${cssMaxPx}px` : undefined,
        },
    indicatorProps: {
      ariaValueMax,
      ariaValueMin: min,
      ariaValueNow,
      direction,
      isDragging,
      onDoubleClick: resetSize,
      onKeyDown: handleKeyDown,
      onLostPointerCapture: stopDragging,
      onPointerCancel: stopDragging,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: stopDragging,
    },
    isDragging,
  };
};
